import { getSearch, push } from 'connected-react-router';
import { GoogleApiWrapper, IProvidedProps } from 'google-maps-react';
import { FC, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { useDispatch, useSelector } from 'react-redux';
import ReactTags, { Tag } from 'react-tag-autocomplete';

import { CoordinateMode } from '../../../../../../models/coordinate.model';
import { updateQueryString } from '../../../../../../providers/location.provider';
import LocationService from '../../../../../../services/LocationService/LocationService';
import {
    bookingYear,
    coordinateData,
    DataSchemeSingleProduct,
    getProductList,
    setProductDetails,
    setProductListWithReset,
    SmartPhrase,
    smartPhrases,
    smartPhrasesList,
    updateCoordinateData,
} from '../../../../../../store/location';
import { abortSearch, setSmartPhrases } from '../../../../../../store/location/actions';

import { AppState } from '../../../../../../store/store';
import { setError, setLoader } from '../../../../../../store/ui';
import { getCordsMap, getSelectPayloadWithFilters, isId } from '../../../../../../utils';
import './index.scss';
import { LOCATION_PATH } from '../../../../Navigator';

const LocationSearchInput: FC<{ classNameContainer: string } & IProvidedProps> = ({ classNameContainer }) => {
    const dispatch = useDispatch();
    const refTags = useRef(null);
    const intl = useIntl();
    const coordinate = useSelector(coordinateData);
    const searchState = useSelector((state: AppState) => getSearch(state));
    const bookingyear = useSelector(bookingYear);
    const phrasesList = useSelector(smartPhrasesList);
    const smartPhrasesDetails = useSelector(smartPhrases);
    const [address, setAddress] = useState('');
    const [tag, setTag] = useState<Tag | null>(null);

    useEffect(() => setTag({ id: 1, name: coordinate.name as string }), [coordinate]);

    const isSmartPhrase = (value: string) => phrasesList.some((el: string) => el.startsWith(value.toLowerCase()));

    const getSmartPhrase = (smartPhrase: string): SmartPhrase[] => {
        const initialPhrases = smartPhrasesDetails.filter(phrase =>
            phrase.phrase.toLowerCase().startsWith(smartPhrase.toLowerCase())
        );
        const result: SmartPhrase[] = [];
        const usedLabels: string[] = [];

        for (const phrase of initialPhrases) {
            if (!usedLabels.includes(phrase.showlabel)) {
                usedLabels.push(phrase.showlabel);
                result.push(phrase);
            }
        }

        return result;
    };

    const handleSelect = ({ name, id }: Tag) => {
        dispatch(abortSearch());

        if (!isNaN(+name) && name.length > 4) {
            id = name;
            name = 'ID:' + name;
        }

        if (isSmartPhrase(name.split(' - ')[0])) {
            dispatch(setProductDetails({ data: null } as unknown as DataSchemeSingleProduct));
            const smartPhrase = getSmartPhrase(name.split(' - ')[0]);
            dispatch(setSmartPhrases(smartPhrase));

            const selectPayload = getSelectPayloadWithFilters(coordinate, searchState, {});
            const searchPhraseCriteria = smartPhrase[id as number].selectcriteria;
            const targetPhrase = smartPhrase[id as number].phrase;

            for (const searchCriteria of Object.keys(searchPhraseCriteria)) {
                const searchCriteriaValue = searchPhraseCriteria[searchCriteria];
                if (Object.keys(selectPayload.criteria).includes(searchCriteria)) {
                    for (const criteria of Object.keys(selectPayload.criteria)) {
                        if (criteria === searchCriteria) {
                            if (searchCriteria === 'coordinate') {
                                ((selectPayload.criteria as never)[searchCriteria] as string[]) = searchCriteriaValue;
                            } else {
                                ((selectPayload.criteria as never)[searchCriteria] as string[]).push(...searchCriteriaValue);
                            }
                            break;
                        }
                    }
                } else {
                    ((selectPayload.criteria as never)[searchCriteria] as string[]) = searchCriteriaValue as string[];
                }
            }
            dispatch(setLoader({ location: true }));
            LocationService.getProductList(selectPayload)
                .then(data => getCordsMap(data))
                .then(data => {
                    dispatch(setProductListWithReset(data));
                    dispatch(
                        updateCoordinateData({
                            name: name.slice(0, name.lastIndexOf(',')),
                            latitude: data.centerCordsMap?.lat,
                            longitude: data.centerCordsMap?.lng,
                            mode: CoordinateMode.Radius,
                            radius: 1000,
                            phrase: targetPhrase,
                        })
                    );
                })
                .catch(err => {
                    dispatch(setError({ location: err }));

                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    if (err.status === 400) {
                        dispatch(setProductListWithReset({ data: { products: [], count: 0, eod: false, groupcount: 0 } }));
                    }
                })
                .finally(() => dispatch(setLoader({ location: false })));
        } else if (isId(name) && id) {
            dispatch(setProductDetails({ data: null } as unknown as DataSchemeSingleProduct));
            void LocationService.getProduct({ bookingyear: bookingyear, urn: id as string })
                .then(({ data }) => {
                    if (data && data[0]) {
                        dispatch(
                            updateCoordinateData({
                                name,
                                latitude: data[0].latitude,
                                longitude: data[0].longitude,
                                mode: CoordinateMode.Radius,
                                individualproduct: data[0].producturn,
                                radius: coordinate.radius ? coordinate.radius : 1000,
                            })
                        );
                    }
                })
                .catch((error: Error) => console.error('Error: ', error));
        } else {
            geocodeByAddress(name)
                .then(async results => {
                    let coordinateMode = CoordinateMode.Radius;
                    const updatedName = name.slice(0, name.lastIndexOf(','));
                    let city = '';

                    if (results[0].types.includes('administrative_area_level_1')) {
                        coordinateMode = CoordinateMode.Kanton;
                    } else if (results[0].types.includes('sublocality')) {
                        coordinateMode = CoordinateMode.Suburb;
                        city = results[0].address_components[1].long_name;
                    } else if (results[0].types.includes('postal_code')) {
                        coordinateMode = CoordinateMode.Zip;
                        city = updatedName.split(' ')[1];
                    } else if (results[0].types.includes('locality')) {
                        coordinateMode = CoordinateMode.City;
                    }

                    return {
                        latLng: await getLatLng(results[0]),
                        non_locality: coordinateMode === CoordinateMode.Radius,
                        mode: coordinateMode,
                        updatedName: updatedName,
                        city: city,
                    };
                })
                .then(({ latLng, non_locality, mode, updatedName, city }) => {
                    dispatch(
                        updateCoordinateData({
                            name: updatedName,
                            latitude: latLng.lat,
                            longitude: latLng.lng,
                            mode: mode,
                            city: city,
                            radius: non_locality ? (coordinate.radius ? coordinate.radius : 1000) : 0,
                        })
                    );
                })
                .catch(error => console.error('Error: ', error));
        }

        handleBack();
    };

    const handleBack = () => {
        dispatch(
            push({
                pathname: LOCATION_PATH,
                search: updateQueryString(searchState, { urn: undefined }),
            })
        );

        // TODO need refinement
        /*const length = urlHistoryState.length;
        const item = length > 1 ? urlHistoryState[length - 2] : null;

        if (item?.includes('/location') && !item?.includes('/location/product')) {
            history.goBack();
        } else {
            dispatch(
                push({
                    pathname: LOCATION_PATH,
                    search: updateQueryString(searchState, { urn: undefined }),
                })
            );
        }*/
    };

    const onDelete = () => {
        dispatch(setLoader({ location: true }));
        dispatch(
            updateCoordinateData({
                name: '',
                latitude: '',
                longitude: '',
                mode: CoordinateMode.Radius,
                radius: 1000,
                phrase: '',
            })
        );
        dispatch(abortSearch());
        setTag(null);
        dispatch(getProductList({ bookingyear: bookingyear, reset: true }));
    };

    const onAddition = (tag: Tag) => {
        const name =
            isId(tag.name) || (!isNaN(+tag.name) && tag.name.length > 4) || isSmartPhrase(tag.name.split(' - ')[0])
                ? tag.name
                : tag.name.slice(0, tag.name.lastIndexOf(','));

        if (coordinate.name !== name) {
            setTag({ ...tag, name });
            handleSelect(tag);
        }
    };

    const googlePlacesFilteringMap = [
        {
            placeId: 'ChIJG5Xt9FZ1kEcRDM3JZY2wXAA',
            idsToHide: [
                'ChIJfXVSQEJ1kEcRWHaIRtZ3a8Y',
                'ChIJj9uc460LkEcRGNpgA6oaO9E',
                'ChIJz6i2L-WfmkcRkwp0dLD87WM',
                'ChIJE6ENJE11kEcRyPgKOu5LxzI',
                'ChIJG5Xt9FZ1kEcRDM3JZY2wXAA',
                'ChIJ1XrBwybmkUcRXbjjLhn1hcE',
            ],
        },
        {
            placeId: 'ChIJfXVSQEJ1kEcRWHaIRtZ3a8Y',
            idsToHide: ['ChIJfXVSQEJ1kEcRWHaIRtZ3a8Y'],
        },
        {
            placeId: 'ChIJ1XrBwybmkUcRXbjjLhn1hcE',
            idsToHide: 'ChIJ1XrBwybmkUcRXbjjLhn1hcE',
        },
    ];

    const googlePlacesExceptions = googlePlacesFilteringMap.map(item => item.placeId);

    return (
        <PlacesAutocomplete
            value={address}
            onChange={setAddress}
            highlightFirstSuggestion
            /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
            // @ts-ignore
            searchOptions={{ componentRestrictions: { country: ['ch', 'li'] as string } }}
        >
            {({ getInputProps, suggestions }) => {
                let filteredSuggestions = [...suggestions];

                if (filteredSuggestions.map(value => value.placeId).some(arr => googlePlacesExceptions.includes(arr))) {
                    for (const exception of googlePlacesExceptions) {
                        const duplicates = googlePlacesFilteringMap.filter(item => item.placeId === exception)[0].idsToHide;
                        filteredSuggestions = [...filteredSuggestions].filter(value => !duplicates.includes(value.placeId));
                    }
                }

                let suggestionsReworked: Array<Tag | { id: number; name: string; types: string[] }> = [
                    ...filteredSuggestions,
                ].map(({ index, description, types }) => ({
                    id: index,
                    name: description.toString(),
                    types,
                }));

                if (!isNaN(+address) && address.length > 4) {
                    suggestionsReworked = [
                        {
                            id: +address,
                            name: address,
                            types: ['individual_product'],
                        },
                        ...suggestionsReworked,
                    ];
                }

                let extraSuggestionsLength = 0;

                if (isSmartPhrase(address) && address.length > 0) {
                    const smartPhraseData = getSmartPhrase(address);
                    let res = [] as Tag[];
                    if (smartPhraseData.length > 0) {
                        res = smartPhraseData.map((phrase: SmartPhrase, i: number) => {
                            const value = `${address.toLocaleUpperCase()} - ${phrase.showlabel} `;
                            return {
                                id: `address-${i}`,
                                name: value,
                                types: ['individual_product'],
                            };
                        });
                    }
                    suggestionsReworked = [...res, ...suggestionsReworked];
                    extraSuggestionsLength = res.length;

                    suggestionsReworked = suggestionsReworked.map((suggestion, i) => {
                        return { ...suggestion, id: i };
                    });
                }
                if (isId(address)) {
                    suggestionsReworked = [
                        {
                            id: +address.replace(/\s/g, '').slice(3),
                            name: address.toLocaleUpperCase(),
                            types: ['individual_product'],
                        },
                        ...suggestionsReworked,
                    ];
                }

                return (
                    <ReactTags
                        classNames={{
                            root: `${classNameContainer}--root`,
                            rootFocused: `${classNameContainer}--rootFocused`,
                            selected: `${classNameContainer}--selected`,
                            selectedTag: `${classNameContainer}--selectedTag`,
                            selectedTagName: `${classNameContainer}--selectedTagName`,
                            search: `${classNameContainer}--search`,
                            searchInput: `${classNameContainer}--searchInput${tag && tag.name ? 'Hidden' : ''}`,
                            suggestions: `${classNameContainer}--suggestions`,
                            suggestionActive: `${classNameContainer}--suggestionActive`,
                            suggestionDisabled: `${classNameContainer}--suggestionDisabled`,
                        }}
                        onInput={val => getInputProps().onChange({ target: { value: val } })}
                        ref={refTags}
                        tags={tag && tag.name ? [tag] : []}
                        suggestions={suggestionsReworked}
                        onDelete={onDelete}
                        onAddition={onAddition}
                        removeButtonText={''}
                        maxSuggestionsLength={6 + extraSuggestionsLength}
                        placeholderText={intl.formatMessage({
                            id: 'searchFilter.inputPlaceholder',
                            defaultMessage: 'PLZ, Ort oder ID',
                        })}
                    />
                );
            }}
        </PlacesAutocomplete>
    );
};

export default GoogleApiWrapper({
    apiKey: process.env.REACT_APP_PLACES_API_KEY as string,
    libraries: ['places'],
})(LocationSearchInput);
